import {Box, Card, Flex, Group, ThemeIcon, Title} from '@mantine/core';
import type {HTMLAttributes, ReactNode} from 'react';
import type {FC} from 'react';

import classes from './SectionCard.module.css';

/**
 * Bordered card with a titled header row (optional icon + right-aligned
 * content) above the card body.
 * @registry Card container with icon/title header and optional right-aligned actions.
 *
 * Ported from `library-m/layout/SectionCard`. Prop surface kept identical.
 */
export interface SectionCardProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  icon?: ReactNode;
  iconColor?: string;
  rightSection?: ReactNode;
  /** Sits with the title on its row (count badge, status pill), never wrapping away from it. */
  titleAdornment?: ReactNode;
  children: ReactNode;
  h?: string | number;
  /** When set, the whole header row becomes clickable (e.g. collapse toggle). */
  onHeaderClick?: () => void;
}

export const SectionCard: FC<SectionCardProps> = ({
  title,
  icon,
  iconColor = 'gray',
  rightSection,
  titleAdornment,
  children,
  h,
  onHeaderClick,
  ...rest
}) => {
  return (
    <Card shadow="sm" withBorder p={0} h={h} {...rest}>
      <Box
        p="md"
        onClick={onHeaderClick}
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-2)',
          cursor: onHeaderClick ? 'pointer' : undefined,
        }}>
        <Flex
          align="center"
          justify="space-between"
          gap="sm"
          wrap="wrap"
          className={classes.header}>
          <Group gap="xs" className={classes.titleGroup}>
            {icon && (
              <ThemeIcon size="sm" variant="light" color={iconColor}>
                {icon}
              </ThemeIcon>
            )}
            <Title order={5}>{title}</Title>
            {titleAdornment}
          </Group>
          {rightSection}
        </Flex>
      </Box>
      {children}
    </Card>
  );
};
